import { createSignal } from 'solid-js';
import { apiEndpoints } from '@config/env';
import { debug, warn } from './console';
import { user } from '../store/userStore';
import { persistantStore } from '../store/persistantStore';

const ALLOWLIST = new Set([
  'RACESUMMARY',
  'PERFORMANCE',
  'MANEUVERS',
  'FLEETPERFORMANCE',
  'FLEETMANEUVERS',
  'TRAININGSUMMARY',
  'PRESTART',
  'STARTSUMMARY',
  'DATASETS',
  'DATASET',
  'EVENTRACESUMMARY',
  'PERFORMANCEHISTORY',
  'MANEUVERSHISTORY',
  'FLEETPERFORMANCEHISTORY',
  'FLEETMANEUVERSHISTORY',
  'SUMMARYTABLES',
  'FLEETSUMMARYTABLES',
  'LIVEMANEUVERS',
  'LIVEPERFORMANCE',
]);

function normalizeMenu(menu: string | null | undefined): string {
  return String(menu || '')
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
}

export function isCapitanFloatingMenu(menu: string | null | undefined): boolean {
  return ALLOWLIST.has(normalizeMenu(menu));
}

const [healthOk, setHealthOk] = createSignal<boolean | null>(null);
let lastProbe = 0;
let probeInFlight: Promise<boolean> | null = null;

const OK_TTL_MS = 60_000;
const DOWN_TTL_MS = 20_000;

export async function probeAssistantHealth(force = false): Promise<boolean> {
  const now = Date.now();
  const prev = healthOk();
  const ttl = prev ? OK_TTL_MS : DOWN_TTL_MS;
  if (!force && prev !== null && now - lastProbe < ttl) {
    return prev;
  }
  if (probeInFlight) return probeInFlight;

  probeInFlight = (async () => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(apiEndpoints.assistant.health, {
        method: 'GET',
        credentials: 'include',
        signal: ctrl.signal,
      });
      clearTimeout(t);
      let body: { status?: string; provider?: string } | null = null;
      try {
        body = (await res.json()) as { status?: string; provider?: string };
      } catch {
        body = null;
      }
      // Button only when provider has a key (status ok). Degraded/missing_key hides Capitan.
      const ok =
        res.ok &&
        (body?.status === 'ok' || body?.status === 'success') &&
        body?.provider === 'ok';
      setHealthOk(ok);
      lastProbe = Date.now();
      debug('[Capitan] health', { ok, status: body?.status, provider: body?.provider });
      return ok;
    } catch (err) {
      warn('[Capitan] health probe failed', (err as Error)?.message);
      setHealthOk(false);
      lastProbe = Date.now();
      return false;
    } finally {
      probeInFlight = null;
    }
  })();

  return probeInFlight;
}

export function assistantHealthCached(): boolean | null {
  return healthOk();
}

export function canUseCapitan(): boolean {
  const u = user();
  if (!u) return false;
  const projectId = persistantStore.selectedProjectId();
  if (!projectId) return false;
  if (u.is_super_user) return true;
  const perms = u.permissions as Record<string, string> | undefined;
  if (perms && (perms[String(projectId)] || perms[projectId as unknown as string])) return true;
  // Highest permission any project
  if (u.permissions_highest) return true;
  return !!projectId;
}

export async function shouldShowCapitanFloatingButton(opts: {
  menu: string | null | undefined;
  rightPanelMenu?: string | null;
  isSplitView?: boolean;
}): Promise<boolean> {
  if (!isCapitanFloatingMenu(opts.menu)) return false;
  if (!canUseCapitan()) return false;
  if (opts.isSplitView && normalizeMenu(opts.rightPanelMenu) === 'ASSISTANT') return false;
  return probeAssistantHealth();
}
